/**
 * InviteAcceptPage
 *
 * Shown to already-authenticated users (who already belong to a family) when
 * they click a /join/:familyCode/:inviteCode invite link.
 *
 * New users (no family yet) still go through AccountCreationWizard instead.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Avatar,
} from '@mui/material';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useAuth } from '../contexts/AuthContext';
import { invitationService } from '../services/invitationService';
import { subscriptionService } from '../services/subscriptionService';
import logger from '../utils/logger';

const InviteAcceptPage = () => {
  const navigate = useNavigate();
  const { familyCode: urlFamilyCode, inviteCode } = useParams();
  const { currentUser, subscription, refreshUserData } = useAuth();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadInvite = async () => {
      try {
        const inv = await invitationService.getInvitation(inviteCode, urlFamilyCode);
        setInvitation(inv);
      } catch (err) {
        logger.error('Failed to load invitation:', err);
        setError('This invitation is invalid or has already been used.');
      } finally {
        setLoading(false);
      }
    };
    if (inviteCode) {
      loadInvite();
    }
  }, [inviteCode, urlFamilyCode]);

  const handleAccept = async () => {
    if (!currentUser || !inviteCode) return;
    setAccepting(true);
    setError(null);
    try {
      await invitationService.acceptInvitation(inviteCode, currentUser.uid, {
        email: currentUser.email,
        firstName: currentUser.firstName || currentUser.displayName?.split(' ')[0] || '',
        lastName: currentUser.lastName || currentUser.displayName?.split(' ').slice(1).join(' ') || '',
        phone: currentUser.phone || null,
        displayName: currentUser.displayName || currentUser.email,
      }, urlFamilyCode);

      // Give invited members a trial if they don't already have one
      if (!subscription) {
        try {
          await subscriptionService.initializeFreeSubscription(currentUser.uid);
        } catch (subErr) {
          logger.error('Failed to init trial for existing user:', subErr);
        }
      }

      // Refresh so currentUser picks up the new familyCode / currentFamily
      await refreshUserData({ waitForField: 'familyCode' });

      setAccepted(true);
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      logger.error('Failed to accept invitation:', err);
      setError(err.message || 'Failed to accept invitation. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (accepted) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" px={2}>
        <Paper elevation={3} sx={{ maxWidth: 480, width: '100%', p: 4, textAlign: 'center', borderRadius: 3 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
          <Typography variant="h5" fontWeight={700} gutterBottom>
            You've joined the family!
          </Typography>
          <Typography color="text.secondary">
            Taking you home…
          </Typography>
        </Paper>
      </Box>
    );
  }

  const inviterName = invitation?.inviterName || invitation?.invitedByName || 'A family member';
  const targetFamily = invitation?.familyCode || urlFamilyCode || '';
  const currentFamily = currentUser?.familyCode || currentUser?.currentFamily || '';

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" px={2}>
      <Paper elevation={3} sx={{ maxWidth: 480, width: '100%', p: 4, textAlign: 'center', borderRadius: 3 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 64, height: 64, mx: 'auto', mb: 2 }}>
          <FamilyRestroomIcon sx={{ fontSize: 36 }} />
        </Avatar>

        <Typography variant="h5" fontWeight={700} gutterBottom>
          Family Invitation
        </Typography>

        {error ? (
          <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>{error}</Alert>
        ) : (
          <>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>{inviterName}</strong> has invited you to join family{' '}
              <strong>{targetFamily}</strong>.
            </Typography>

            {currentFamily && currentFamily !== targetFamily && (
              <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
                You're currently in family <strong>{currentFamily}</strong>. Accepting this
                invitation will add you to <strong>{targetFamily}</strong> as well.
              </Alert>
            )}

            {invitation?.relationship && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Relationship: <strong>{invitation.relationship}</strong>
              </Typography>
            )}

            {invitation?.customMessage && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  "{invitation.customMessage}"
                </Typography>
              </>
            )}
          </>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mt: 3 }}>
          {!error && (
            <Button
              variant="contained"
              size="large"
              onClick={handleAccept}
              disabled={accepting}
              startIcon={accepting ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {accepting ? 'Joining…' : 'Accept Invitation'}
            </Button>
          )}
          <Button variant="outlined" size="large" onClick={() => navigate('/')}>
            {error ? 'Go Home' : 'Decline'}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default InviteAcceptPage;
